import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { useEditorStore, type ElementMap } from '../stores/useEditorStore';
import {
  distance,
  normalizeRadians,
  point,
  pointCenter,
  pointRotateRads,
  type Point,
} from '../utils/math';

import { getCommonBounds, getElementBounds } from './bound';
import { getConfig } from './config';
import {
  reversionElement,
  updateElement,
  updateMultipleElements,
  type GenericElement,
} from './element';
import { getPlugin } from './plugin';
import {
  type TransformHandleDirection,
  type TransformHandleType,
} from './selection';

export const dragNewElement = (payload: {
  originX: number;
  originY: number;
  x: number;
  y: number;
}) => {
  const { originX, originY, x, y } = payload;

  const newWidth = distance(x, originX);
  const newHeight = distance(y, originY);

  const newX = x < originX ? originX - newWidth : originX;
  const newY = y < originY ? originY - newHeight : originY;

  return { height: newHeight, width: newWidth, x: newX, y: newY };
};

export const moveMultipleElements = (
  elements: GenericElement[],
  [pointerX, pointerY]: Point,
  [originX, originY]: Point,
) => {
  const elementState = useEditorStore.getState();

  const { activeKeyPress } = useEditorRuntimeStore.getState();

  const getPointer = () => {
    if (!activeKeyPress?.shift) return [pointerX, pointerY];
    return [pointerX - (pointerX % 100), pointerY - (pointerY % 100)];
  };

  const [newPointerX, newPointerY] = getPointer();

  const updates = elements.map((element) => {
    const updatedElement = {
      ...element,
      x: element.originX + newPointerX - originX,
      y: element.originY + newPointerY - originY,
      ...reversionElement(element, ['bounds', 'canvas']),
    };
    updatedElement.shape = getPlugin(
      updatedElement.type,
    ).collision.calculateShape(updatedElement);
    return updatedElement;
  });

  updateMultipleElements(elementState.elements, elements, updates);
};

export const rotateSingleElement = (
  element: GenericElement,
  [pointerX, pointerY]: Point,
) => {
  const config = getConfig();
  const { activeKeyPress } = useEditorRuntimeStore.getState();

  const rotateWithDiscreteAngle = activeKeyPress?.shift;

  const { elements } = useEditorStore.getState();
  const plugin = getPlugin(element.type);

  const [, , , , cx, cy] = getElementBounds(element);
  let angle = normalizeRadians(
    (5 * Math.PI) / 2 + Math.atan2(pointerY - cy, pointerX - cx),
  );

  if (rotateWithDiscreteAngle) {
    angle = angle + config.transformation.rotateLockingAngle / 2;
    angle = angle - (angle % config.transformation.rotateLockingAngle);
  }

  updateElement(elements, element, {
    angle,
    shape: plugin.collision.calculateShape(element),
  });
};

export const rotateMultipleElements = (
  elements: GenericElement[],
  originalElements: ElementMap,
  [pointerX, pointerY]: Point,
) => {
  const config = getConfig();
  const state = useEditorStore.getState();
  const { activeKeyPress } = useEditorRuntimeStore.getState();

  const rotateWithDiscreteAngle = activeKeyPress?.shift;

  const targetElements = elements.reduce(
    (acc: { latest: GenericElement; orig: GenericElement }[], element) => {
      const origElement = originalElements.get(element.id);
      if (origElement) acc.push({ latest: element, orig: origElement });
      return acc;
    },
    [],
  );

  const [, , , , centerX, centerY] = getCommonBounds(
    targetElements.map(({ orig }) => orig),
  );

  let centerAngle =
    (5 * Math.PI) / 2 + Math.atan2(pointerY - centerY, pointerX - centerX);

  if (rotateWithDiscreteAngle) {
    centerAngle += config.transformation.rotateLockingAngle / 2;
    centerAngle -= centerAngle % config.transformation.rotateLockingAngle;
  }

  const updatedElements = elements.map((element) => {
    const [, , , , cx, cy] = getElementBounds(element, { withRotation: false });

    const origAngle = originalElements.get(element.id)?.angle ?? element.angle;

    const [rotatedCX, rotatedCY] = pointRotateRads(
      point(cx, cy),
      point(centerX, centerY),
      centerAngle + origAngle - element.angle,
    );

    const elementPlugin = getPlugin(element.type);

    if (elementPlugin.transformation.manualRotation) {
      const originalElement = originalElements.get(element.id);
      if (!originalElement) return element;

      return elementPlugin.transformation.manualRotation(
        element,
        originalElement,
        [centerX, centerY],
        centerAngle,
      );
    }

    const newElement = {
      ...element,
      angle: normalizeRadians(centerAngle + origAngle),
      x: element.x + (rotatedCX - cx),
      y: element.y + (rotatedCY - cy),
      ...reversionElement(element, ['bounds']),
    };

    newElement.shape = getPlugin(newElement.type).collision.calculateShape(
      newElement,
    )!;

    return newElement;
  });

  updateMultipleElements(state.elements, elements, updatedElements);
};

export const resizeSingleElement = (
  element: GenericElement,
  originalElements: ElementMap,
  [pointerX, pointerY]: Point,
  transformHandleDirection: TransformHandleDirection,
) => {
  const { activeKeyPress } = useEditorRuntimeStore.getState();
  const { elements } = useEditorStore.getState();
  const plugin = getPlugin(element.type);

  const shouldMaintainAspectRatio =
    activeKeyPress?.shift || plugin.transformation.alwaysKeepAspectRatio;
  const shouldResizeFromCenter = activeKeyPress?.alt;

  const stateAtResizeStart = originalElements.get(element.id)!;

  const [x1, y1, x2, y2] = plugin.transformation.resizedBounds(
    stateAtResizeStart,
    stateAtResizeStart.width,
    stateAtResizeStart.height,
  );
  const startTopLeft = point(x1, y1);
  const startBottomRight = point(x2, y2);
  const startCenter = pointCenter(startTopLeft, startBottomRight);

  // Calculate new dimensions based on cursor position
  const rotatedPointer = pointRotateRads(
    point(pointerX, pointerY),
    startCenter,
    -stateAtResizeStart.angle,
  );

  // Get bounds corners rendered on screen
  const [esx1, esy1, esx2, esy2] = plugin.transformation.resizedBounds(
    element,
    element.width,
    element.height,
  );
  const boundsCurrentWidth = esx2 - esx1;
  const boundsCurrentHeight = esy2 - esy1;

  // It's important we set the initial scale value based on the width and height at resize start,
  // otherwise previous dimensions affected by modifiers will be taken into account.
  const atStartBoundsWidth = startBottomRight[0] - startTopLeft[0];
  const atStartBoundsHeight = startBottomRight[1] - startTopLeft[1];
  let scaleX = atStartBoundsWidth / boundsCurrentWidth;
  let scaleY = atStartBoundsHeight / boundsCurrentHeight;

  if (transformHandleDirection.includes('e')) {
    scaleX = (rotatedPointer[0] - startTopLeft[0]) / boundsCurrentWidth;
  }
  if (transformHandleDirection.includes('s')) {
    scaleY = (rotatedPointer[1] - startTopLeft[1]) / boundsCurrentHeight;
  }
  if (transformHandleDirection.includes('w')) {
    scaleX = (startBottomRight[0] - rotatedPointer[0]) / boundsCurrentWidth;
  }
  if (transformHandleDirection.includes('n')) {
    scaleY = (startBottomRight[1] - rotatedPointer[1]) / boundsCurrentHeight;
  }

  const eleInitialWidth = stateAtResizeStart.width;
  const eleInitialHeight = stateAtResizeStart.height;

  // We have to use dimensions of element on screen, otherwise the scaling of the
  // dimensions won't match the cursor for linear elements.
  let eleNewWidth = element.width * scaleX;
  let eleNewHeight = element.height * scaleY;

  // adjust dimensions for resizing from center
  if (shouldResizeFromCenter) {
    eleNewWidth = 2 * eleNewWidth - eleInitialWidth;
    eleNewHeight = 2 * eleNewHeight - eleInitialHeight;
  }

  // adjust dimensions to keep sides ratio
  if (shouldMaintainAspectRatio) {
    const widthRatio = Math.abs(eleNewWidth) / eleInitialWidth;
    const heightRatio = Math.abs(eleNewHeight) / eleInitialHeight;
    if (transformHandleDirection.length === 1) {
      eleNewHeight *= widthRatio;
      eleNewWidth *= heightRatio;
    }
    if (transformHandleDirection.length === 2) {
      const ratio = Math.max(widthRatio, heightRatio);
      eleNewWidth = eleInitialWidth * ratio * Math.sign(eleNewWidth);
      eleNewHeight = eleInitialHeight * ratio * Math.sign(eleNewHeight);
    }
  }

  const [newBoundsX1, newBoundsY1, newBoundsX2, newBoundsY2] =
    plugin.transformation.resizedBounds(
      stateAtResizeStart,
      eleNewWidth,
      eleNewHeight,
    );

  const newBoundsWidth = newBoundsX2 - newBoundsX1;
  const newBoundsHeight = newBoundsY2 - newBoundsY1;
  let newTopLeft = [...startTopLeft] as [number, number];

  if (['n', 'nw', 'w'].includes(transformHandleDirection)) {
    newTopLeft = [
      startBottomRight[0] - Math.abs(newBoundsWidth),
      startBottomRight[1] - Math.abs(newBoundsHeight),
    ];
  }

  if (transformHandleDirection === 'ne') {
    const bottomLeft = [startTopLeft[0], startBottomRight[1]];
    newTopLeft = [bottomLeft[0], bottomLeft[1] - Math.abs(newBoundsHeight)];
  }

  if (transformHandleDirection === 'sw') {
    const topRight = [startBottomRight[0], startTopLeft[1]];
    newTopLeft = [topRight[0] - Math.abs(newBoundsWidth), topRight[1]];
  }

  if (shouldMaintainAspectRatio) {
    if (['n', 's'].includes(transformHandleDirection)) {
      newTopLeft[0] = startCenter[0] - newBoundsWidth / 2;
    }
    if (['e', 'w'].includes(transformHandleDirection)) {
      newTopLeft[1] = startCenter[1] - newBoundsHeight / 2;
    }
  }

  const flipX = eleNewWidth < 0;
  const flipY = eleNewHeight < 0;

  // Flip horizontally
  if (flipX) {
    if (transformHandleDirection.includes('e')) {
      newTopLeft[0] -= Math.abs(newBoundsWidth);
    }
    if (transformHandleDirection.includes('w')) {
      newTopLeft[0] += Math.abs(newBoundsWidth);
    }
  }

  // Flip vertically
  if (flipY) {
    if (transformHandleDirection.includes('s')) {
      newTopLeft[1] -= Math.abs(newBoundsHeight);
    }
    if (transformHandleDirection.includes('n')) {
      newTopLeft[1] += Math.abs(newBoundsHeight);
    }
  }

  if (shouldResizeFromCenter) {
    newTopLeft[0] = startCenter[0] - Math.abs(newBoundsWidth) / 2;
    newTopLeft[1] = startCenter[1] - Math.abs(newBoundsHeight) / 2;
  }

  // adjust topLeft to new rotation point
  const angle = stateAtResizeStart.angle;
  const rotatedTopLeft = pointRotateRads(
    point(...newTopLeft),
    startCenter,
    angle,
  );
  const newCenter = point(
    newTopLeft[0] + Math.abs(newBoundsWidth) / 2,
    newTopLeft[1] + Math.abs(newBoundsHeight) / 2,
  );
  const rotatedNewCenter = pointRotateRads(newCenter, startCenter, angle);
  newTopLeft = pointRotateRads(rotatedTopLeft, rotatedNewCenter, -angle);

  const newOrigin = [...newTopLeft];
  const linearElementXOffset = stateAtResizeStart.x - newBoundsX1;
  const linearElementYOffset = stateAtResizeStart.y - newBoundsY1;
  newOrigin[0] += linearElementXOffset;
  newOrigin[1] += linearElementYOffset;

  const nextX = newOrigin[0];
  const nextY = newOrigin[1];

  const resizedElement = {
    height: Math.abs(eleNewHeight),
    width: Math.abs(eleNewWidth),
    x: nextX,
    y: nextY,
    ...plugin.transformation.onSingleResize?.(
      element,
      stateAtResizeStart,
      eleNewWidth,
      eleNewHeight,
      transformHandleDirection,
    ),
  };

  updateElement(elements, element, {
    ...(element.scale && stateAtResizeStart.scale
      ? {
          scale: [
            // defaulting because scaleX/Y can be 0/-0
            (Math.sign(newBoundsX2 - stateAtResizeStart.x) ||
              stateAtResizeStart.scale[0]) * stateAtResizeStart.scale[0],
            (Math.sign(newBoundsY2 - stateAtResizeStart.y) ||
              stateAtResizeStart.scale[1]) * stateAtResizeStart.scale[1],
          ],
        }
      : {}),
    ...(resizedElement.width !== 0 &&
    resizedElement.height !== 0 &&
    Number.isFinite(resizedElement.x) &&
    Number.isFinite(resizedElement.y)
      ? resizedElement
      : {}),
    shape: plugin.collision.calculateShape(element),
    ...reversionElement(element, ['bounds', 'canvas']),
  });
};

export const resizeMultipleElements = (
  elements: GenericElement[],
  originalElements: ElementMap,
  [pointerX, pointerY]: Point,
  direction: TransformHandleDirection,
) => {
  const { activeKeyPress } = useEditorRuntimeStore.getState();
  const state = useEditorStore.getState();

  const shouldMaintainAspectRatio = activeKeyPress?.shift;
  const shouldResizeFromCenter = activeKeyPress?.alt;

  // map selected elements to the original elements. While it never should happen that
  // pointerDownState.originalElements won't contain the selected  elements during resize,
  // this coupling isn't guaranteed, so to ensure type safety we need to transform only those elements we filter.
  const targetElements = elements.reduce(
    (acc: { latest: GenericElement; orig: GenericElement }[], element) => {
      const origElement = originalElements.get(element.id);
      if (origElement) acc.push({ latest: element, orig: origElement });
      return acc;
    },
    [],
  );

  const [minX, minY, maxX, maxY, midX, midY] = getCommonBounds(
    targetElements.map(({ orig }) => orig),
  );
  const width = maxX - minX;
  const height = maxY - minY;

  const anchorsMap: Record<TransformHandleDirection, Point> = {
    e: point(minX, minY + height / 2),
    n: point(minX + width / 2, maxY),
    ne: point(minX, maxY),
    nw: point(maxX, maxY),
    s: point(minX + width / 2, minY),
    se: point(minX, minY),
    sw: point(maxX, minY),
    w: point(maxX, minY + height / 2),
  };

  // anchor point must be on the opposite side of the dragged selection handle
  // or be the center of the selection if shouldResizeFromCenter
  const [anchorX, anchorY] = shouldResizeFromCenter
    ? point(midX, midY)
    : anchorsMap[direction];

  const resizeFromCenterScale = shouldResizeFromCenter ? 2 : 1;

  const scale =
    Math.max(
      Math.abs(pointerX - anchorX) / width || 0,
      Math.abs(pointerY - anchorY) / height || 0,
    ) * resizeFromCenterScale;

  if (scale === 0) {
    return;
  }

  let scaleX =
    direction.includes('e') || direction.includes('w')
      ? (Math.abs(pointerX - anchorX) / width) * resizeFromCenterScale
      : 1;
  let scaleY =
    direction.includes('n') || direction.includes('s')
      ? (Math.abs(pointerY - anchorY) / height) * resizeFromCenterScale
      : 1;

  const keepAspectRatio =
    shouldMaintainAspectRatio ||
    targetElements.some((item) => item.latest.angle !== 0);

  if (keepAspectRatio) {
    scaleX = scale;
    scaleY = scale;
  }

  const flipConditionsMap: Record<
    TransformHandleDirection,
    // Condition for which we should flip or not flip the selected elements
    // - when evaluated to `true`, we flip
    // - therefore, setting it to always `false` means we do not flip (in that direction) at all
    [x: boolean, y: boolean]
  > = {
    // e.g. when resizing from the "e" side, we do not need to consider changes in the `y` direction
    //      and therefore, we do not need to flip in the `y` direction at all
    e: [pointerX < anchorX, false],
    n: [false, pointerY > anchorY],
    ne: [pointerX < anchorX, pointerY > anchorY],
    nw: [pointerX > anchorX, pointerY > anchorY],
    s: [false, pointerY < anchorY],
    se: [pointerX < anchorX, pointerY < anchorY],
    sw: [pointerX > anchorX, pointerY < anchorY],
    w: [pointerX > anchorX, false],
  };

  /**
   * to flip an element:
   * 1. determine over which axis is the element being flipped
   *    (could be x, y, or both) indicated by `flipFactorX` & `flipFactorY`
   * 2. shift element's position by the amount of width or height (or both) or
   *    mirror points in the case of linear & freedraw elements
   * 3. adjust element angle
   */
  const [flipFactorX, flipFactorY] = flipConditionsMap[direction].map(
    (condition) => (condition ? -1 : 1),
  );

  const isFlippedByX = flipFactorX < 0;
  const isFlippedByY = flipFactorY < 0;

  const updates = Array.from(targetElements.values()).map(
    ({ latest, orig }) => {
      const plugin = getPlugin(latest.type);

      const width = orig.width * scaleX;
      const height = orig.height * scaleY;
      const angle = normalizeRadians(orig.angle * flipFactorX * flipFactorY);

      const offsetX = orig.x - anchorX;
      const offsetY = orig.y - anchorY;
      const shiftX =
        isFlippedByX && plugin.transformation.shiftBySizeOnFlip ? width : 0;
      const shiftY =
        isFlippedByY && plugin.transformation.shiftBySizeOnFlip ? height : 0;
      const x = anchorX + flipFactorX * (offsetX * scaleX + shiftX);
      const y = anchorY + flipFactorY * (offsetY * scaleY + shiftY);

      return {
        angle,
        height,
        width,
        x,
        y,
        ...plugin.transformation.onMultiResize?.(
          latest,
          orig,
          width * flipFactorX,
          height * flipFactorY,
          direction,
        ),
        ...(latest.scale && orig.scale
          ? {
              scale: [orig.scale[0] * flipFactorX, orig.scale[1] * flipFactorY],
            }
          : {}),
        ...reversionElement(latest, ['bounds', 'canvas']),
      };
    },
  ) as Partial<GenericElement>[];

  updateMultipleElements(state.elements, elements, updates);
};

export const transformElements = (
  transform: 'move' | TransformHandleType,
  selectedElements: GenericElement[],
  originalElements: ElementMap,
  [pointerX, pointerY]: Point,
  [originX, originY]: Point,
) => {
  if (selectedElements.length === 1) {
    if (transform === 'move') {
      moveMultipleElements(
        selectedElements,
        [pointerX, pointerY],
        [originX, originY],
      );
    } else if (transform === 'rotation') {
      rotateSingleElement(selectedElements[0], [pointerX, pointerY]);
    } else {
      resizeSingleElement(
        selectedElements[0],
        originalElements,
        [pointerX, pointerY],
        transform as TransformHandleDirection,
      );
    }

    return;
  }

  if (transform === 'move') {
    moveMultipleElements(
      selectedElements,
      [pointerX, pointerY],
      [originX, originY],
    );
  } else if (transform === 'rotation') {
    rotateMultipleElements(selectedElements, originalElements, [
      pointerX,
      pointerY,
    ]);
  } else {
    resizeMultipleElements(
      selectedElements,
      originalElements,
      [pointerX, pointerY],
      transform as TransformHandleDirection,
    );
  }
};
